import isEqual from 'react-fast-compare';

interface FilterNotEmptyOptions {
  removeEmptyStrings?: boolean;
  removeEmptyArrays?: boolean;
}

export function filterNotEmpty<MODEL>(
  values: (MODEL | undefined | null | false)[] | undefined | null,
  options?: FilterNotEmptyOptions
): MODEL[] {
  return (values ?? []).filter(notEmpty(options));
}

export function notEmpty<MODEL>(
  options: FilterNotEmptyOptions = {}
): (value: MODEL | null | undefined | false) => value is MODEL {
  // @ts-ignore TS doesn't like nested assertions
  return (value) => {
    const result = !(
      value === null ||
      value === undefined ||
      value === false ||
      (options?.removeEmptyStrings && typeof value === 'string' && value.trim() === '') ||
      (options?.removeEmptyArrays && Array.isArray(value) && value?.length === 0)
    );
    return result;
  };
}

// Remove duplicates from array of objects
export const arrayUniqueByKey = <T extends any, K extends keyof T>(array: T[], key: K): T[] => [
  ...new Map(array.map((item) => [item[key], item])).values()
];

export const arrayUnique = <T extends string>(array: T[]): T[] => [...new Set(array).values()];

export function reorderListItems<T>(list: T[], startIndex: number, endIndex: number) {
  const result = [...list];
  if (startIndex === endIndex) return result;
  const [removed] = result.splice(startIndex, 1);
  if (removed) {
    result.splice(endIndex, 0, removed);
  }
  return result;
}

export function removeListItemByIndex<T>(list: T[], index: number) {
  const result = [...list];
  const removed = result.splice(index, 1);
  return { result, removed };
}

export function addListItemByIndex<T>(list: T[], items: T[], index: number) {
  const result = [...list];
  result.splice(index, 0, ...items);
  return result;
}

/**
 * A utility class for performing various operations on lists in a chainable manner.
 * It provides methods for filtering, mapping, sorting, and more, allowing for a functional programming style when working with arrays.
 */
export class ListStream {
  static from<T>(list: T[]) {
    return new ListStreamImpl<T>(list ?? []);
  }

  static compact<T>(list: T[] | undefined | null) {
    return new ListStreamImpl<T>(list ?? []).compact();
  }

  static toCompact<T>(list: T[] | undefined | null) {
    return new ListStreamImpl<T>(list ?? []).toCompact();
  }
}

interface InternalSettings {
  ifCondition?: {
    isTrue: boolean;
    currentCondition: 'if' | 'else';
  };
}

type CompactValues<T> = Exclude<T, undefined | null | false>;

class ListStreamImpl<T> {
  private scopedList: T[] = [];
  private internalSettings: InternalSettings = {};

  constructor(list: T[], internalSettings: InternalSettings = {}) {
    this.scopedList = list;
    this.internalSettings = internalSettings;
  }

  private canExecute() {
    const { ifCondition } = this.internalSettings;
    if (!ifCondition) {
      return true;
    }
    const { isTrue, currentCondition } = ifCondition;

    if (currentCondition === 'if' && isTrue) {
      return true;
    }
    return currentCondition === 'else' && !isTrue;
  }

  private execute<NEXT_T>(items: NEXT_T[], internalSettings?: Partial<InternalSettings>) {
    if (!this.canExecute()) {
      return new ListStreamImpl<T>(this.scopedList, this.internalSettings);
    }
    return new ListStreamImpl<NEXT_T>(items, { ...this.internalSettings, ...internalSettings });
  }

  pipe(fn: (list: T[]) => T[]) {
    return this.execute(fn(this.scopedList));
  }

  uniqueByKey<K extends keyof T>(key: K) {
    return this.execute(arrayUniqueByKey(this.scopedList, key));
  }

  findBy<K extends keyof T>(key: K, value: T[K]) {
    const item = this.scopedList.find((item) => isEqual(item[key], value));
    return new ListItemStreamImpl(item);
  }

  filterBy<K extends keyof T>(key: K, value: T[K] | T[K][], type: 'equal' | 'not-equal' = 'equal') {
    return this.execute(
      this.scopedList.filter((item) => {
        if (Array.isArray(value)) {
          const included = value.some((v) => isEqual(item[key], v));
          return type === 'equal' ? included : !included;
        }
        const equal = isEqual(item[key], value);
        return type === 'equal' ? equal : !equal;
      })
    );
  }

  filter(fn: (item: T) => unknown) {
    return this.execute(this.scopedList.filter(fn));
  }

  findIndex<K extends keyof T>(key: K, value: T[K] | T[K][]) {
    return this.scopedList.findIndex((item) => {
      if (Array.isArray(value)) {
        return value.some((v) => isEqual(item[key], v));
      }
      return isEqual(item[key], value);
    });
  }

  push(...items: T[]) {
    return this.execute([...this.scopedList, ...items]);
  }

  sort(compareFn: (a: T, b: T) => number) {
    return this.execute(this.scopedList.toSorted(compareFn));
  }

  if(condition: boolean | ((items: T[]) => boolean)) {
    const isTrue = typeof condition === 'function' ? condition(this.scopedList) : condition;
    return new ListStreamImpl(this.scopedList, {
      ...this.internalSettings,
      ifCondition: {
        isTrue,
        currentCondition: 'if'
      }
    });
  }

  else() {
    return new ListStreamImpl(this.scopedList, {
      ...this.internalSettings,
      ifCondition: {
        isTrue: this.internalSettings.ifCondition?.isTrue ?? false,
        currentCondition: 'else'
      }
    });
  }

  /**
   * Creates a new ListStreamImpl instance with the results of applying the provided callback function to each element in the current list.
   * @param cb - A callback function that takes the current value, index, and the entire array as arguments and returns a new value of type NEXT_T. This function is applied to each element in the list, and the resulting values are used to create a new ListStreamImpl instance with the updated list.
   * @returns A new ListStreamImpl instance containing the results of the mapping operation, allowing for further chainable operations to be performed on the transformed list.
   */
  map<NEXT_T>(cb: (value: T, index: number, array: T[]) => NEXT_T): ListStreamImpl<NEXT_T> {
    return this.execute<NEXT_T>(this.scopedList.map(cb)) as unknown as ListStreamImpl<NEXT_T>;
  }

  /**
   * Ends the current conditional block (if/else) and returns a new ListStreamImpl instance with the original list and updated internal settings,
   * allowing for further operations to be performed on the list after the conditional block.
   */
  endIf() {
    return new ListStreamImpl(this.scopedList, {
      ...this.internalSettings,
      ifCondition: undefined
    });
  }

  /**
   * Returns the final result after performing a series of operations using the ListStream class.
   */
  value() {
    return this.scopedList.slice();
  }

  toCompact(): ListStreamImpl<CompactValues<T>> {
    return this.execute(this.compact()) as unknown as ListStreamImpl<CompactValues<T>>;
  }

  /**
   * Returns a new array containing only the non-empty values from the original list. Non-empty values are those that are not `undefined`, `null`, or `false`.
   */
  compact(): CompactValues<T>[] {
    return filterNotEmpty(this.scopedList).slice() as CompactValues<T>[];
  }
}

class ListItemStreamImpl<T> {
  private scopedItem: T | undefined;

  constructor(item: T | undefined) {
    this.scopedItem = item;
  }

  value(): T | undefined {
    return this.scopedItem;
  }
}
